import {
  ComparisonSummary,
  GroceryItem,
  GroceryResultRow,
  GroceryStoreKey,
  StorePriceChip,
  StoreTotal,
  UnitPrice,
} from "./grocery.types";

/**
 * Pure comparison maths for the grocery showcase. Kept free of Angular /
 * RxJS so it can be unit-tested in isolation and reused unchanged if the
 * calculations later move server-side.
 */

/** Stores compared, in display order, with their short labels. */
export const GROCERY_STORES: { key: GroceryStoreKey; label: string }[] = [
  { key: "aldi", label: "Aldi" },
  { key: "woolworths", label: "Woolies" },
  { key: "coles", label: "Coles" },
];

const STORE_LABELS: Record<GroceryStoreKey, string> = GROCERY_STORES.reduce(
  (acc, s) => ({ ...acc, [s.key]: s.label }),
  {} as Record<GroceryStoreKey, string>,
);

/**
 * Dedup key for a list item: its catalog id when it has one, else its
 * normalised (trimmed, case-insensitive) name plus unit and quantity. Custom
 * items have no catalog id, so without a name key they would be re-added on
 * every copy; unit/quantity are included so distinct custom items sharing a
 * name (e.g. "Milk" 1L vs 2L) aren't collapsed and silently dropped.
 */
export function listItemDedupKey(item: {
  genericItemId?: string | null;
  name: string;
  quantity?: number;
  unit?: string;
}): string {
  return item.genericItemId
    ? `id:${item.genericItemId}`
    : `name:${item.name.trim().toLowerCase()}|${item.unit ?? ""}|${item.quantity ?? ""}`;
}

/** Rounds to cents, avoiding binary-float drift on running totals. */
function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/** "3 units ago" / "1 unit ago" — singular/plural aware. */
function ago(value: number, unit: string): string {
  const n = Math.floor(value);
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

/**
 * Human "last updated" label for an ISO timestamp, coarsening as it ages:
 * "just now" → minutes → hours → days → weeks → months → years. A missing or
 * unparseable timestamp (or a future one, from clock skew) yields "" — the
 * caller simply shows nothing. `nowMs` is injectable for deterministic tests.
 */
export function relativeTime(iso: string | undefined, nowMs: number = Date.now()): string {
  if (!iso) {
    return "";
  }
  const then = Date.parse(iso);
  if (Number.isNaN(then)) {
    return "";
  }
  const diff = nowMs - then;
  if (diff < 0) {
    return "";
  }
  if (diff < MINUTE) {
    return "just now";
  }
  if (diff < HOUR) {
    return ago(diff / MINUTE, "minute");
  }
  if (diff < DAY) {
    return ago(diff / HOUR, "hour");
  }
  if (diff < WEEK) {
    return ago(diff / DAY, "day");
  }
  if (diff < MONTH) {
    return ago(diff / WEEK, "week");
  }
  if (diff < YEAR) {
    return ago(diff / MONTH, "month");
  }
  return ago(diff / YEAR, "year");
}

/**
 * Most recent `priceAsOf` timestamp across every store of every item, or
 * `undefined` if none of them have been priced yet. Feeds the "prices
 * updated N ago" freshness label on the list-wide comparison card.
 */
export function latestPriceAsOf(items: GroceryItem[]): string | undefined {
  let latest: string | undefined;
  let latestMs = -Infinity;
  for (const item of items) {
    for (const iso of Object.values(item.priceAsOf ?? {})) {
      if (!iso) {
        continue;
      }
      const ms = Date.parse(iso);
      // Skip unparseable timestamps rather than letting one bad value "win" and
      // then block every later, valid comparison (Date.parse(valid) > NaN is
      // always false, which would otherwise stick `latest` on the bad value).
      if (!Number.isNaN(ms) && ms > latestMs) {
        latest = iso;
        latestMs = ms;
      }
    }
  }
  return latest;
}

/** Returns the store key with the lowest price for the item, or null if none. */
export function cheapestStore(prices: GroceryItem["prices"]): GroceryStoreKey | null {
  let best: GroceryStoreKey | null = null;
  (Object.keys(prices) as GroceryStoreKey[]).forEach((store) => {
    const price = prices[store];
    if (price == null) {
      return;
    }
    if (best === null || price < (prices[best] as number)) {
      best = store;
    }
  });
  return best;
}

/**
 * One chip per store that carries the item, cheapest flagged (never on a lone
 * store — nothing to be cheaper than). `priceAsOf`/`sourceUrls` are optional.
 */
export function priceChips(
  prices: GroceryItem["prices"],
  priceAsOf?: GroceryItem["priceAsOf"],
  sourceUrls?: GroceryItem["sourceUrls"],
): StorePriceChip[] {
  const stores = (Object.keys(prices) as GroceryStoreKey[]).filter(
    (store) => prices[store] != null,
  );
  const best = stores.length > 1 ? cheapestStore(prices) : null;
  return stores.map((store) => {
    const isCheapest = store === best;
    // Single-store views (e.g. a list row's own store tab) still want to
    // highlight that one price as "the best available here" even though
    // isCheapest is false for a lone store — expose it once here instead of
    // each caller re-deriving "only one chip" for itself.
    const isSoleCarrier = stores.length === 1;
    return {
      store,
      label: STORE_LABELS[store],
      price: prices[store] as number,
      isCheapest,
      isSoleCarrier,
      // Derived once here (not left to each template) so a render site can't
      // forget to also check isSoleCarrier and leave a lone-carrier chip
      // unhighlighted.
      isHighlighted: isCheapest || isSoleCarrier,
      priceAsOf: priceAsOf?.[store],
      sourceUrl: sourceUrls?.[store],
    };
  });
}

/** Normalised per-unit price ($/L, $/kg, $/ea) based on the item's cheapest price. */
export function unitPrice(item: GroceryItem): UnitPrice {
  const best = cheapestStore(item.prices);
  const price = best ? (item.prices[best] as number) : 0;
  const label = item.unit === "L" ? "/L" : item.unit === "g" ? "/kg" : "/ea";
  // Guard a zero/invalid quantity — dividing by it yields Infinity/NaN, which
  // then breaks both the rendered label and the best-value comparison. Treat it
  // as an unknown per-unit price (0).
  if (!(item.quantity > 0)) {
    return { value: 0, label };
  }
  if (item.unit === "L") {
    return { value: price / item.quantity, label: "/L" };
  }
  if (item.unit === "g") {
    return { value: (price / item.quantity) * 1000, label: "/kg" };
  }
  return { value: price / item.quantity, label: "/ea" };
}

/**
 * IDs of the best-value items: for each same-category, same-unit group with
 * 2+ comparable members, the cheapest per-unit price (category narrows the
 * comparison so a 1L olive oil isn't pitted against a 2L milk).
 */
export function bestValueIds(items: GroceryItem[]): Set<string> {
  const groups = new Map<string, { item: GroceryItem; value: number }[]>();
  items.forEach((item) => {
    // A 0 value means "no price"/"invalid quantity" (see unitPrice) — exclude
    // it from the group rather than let it lose every comparison.
    const value = unitPrice(item).value;
    if (value <= 0) {
      return;
    }
    // Normalize category so backend casing/whitespace inconsistencies (e.g.
    // "Dairy" vs "dairy ") don't silently split one group into two.
    const category = (item.category ?? "").trim().toLowerCase();
    // No category (common for custom/unlisted list items, or a catalog data
    // gap) still gets grouped — just under its own "uncategorized" bucket per
    // unit (the empty string is never a real category, so it can't collide
    // with one), never merged with a real category. That keeps the
    // cross-category mismatch this grouping exists to prevent scoped to only
    // the uncategorized items themselves (the same risk the whole app already
    // accepted pre-this-grouping), instead of excluding every uncategorized
    // item from ever winning best-value.
    // JSON-encoded rather than concatenated with a separator, so a category
    // that happens to contain the separator character can't collide with a
    // different (category, unit) pair.
    const key = JSON.stringify([category, item.unit]);
    const group = groups.get(key) ?? [];
    group.push({ item, value });
    groups.set(key, group);
  });

  const bestIds = new Set<string>();
  groups.forEach((group) => {
    if (group.length < 2) {
      return;
    }
    let best = group[0];
    group.forEach((entry) => {
      if (entry.value < best.value) {
        best = entry;
      }
    });
    bestIds.add(best.item.id);
  });
  return bestIds;
}

/**
 * Chips + unit price for one catalog item, `isBestValue` defaulted to `false`.
 * Exported for callers (e.g. a paged search) whose caller recomputes
 * `isBestValue` itself over a wider set of items — building it here via
 * {@link bestValueIds} would be a same-page-only answer that gets thrown away.
 */
export function buildResultRow(item: GroceryItem): GroceryResultRow {
  return {
    item,
    chips: priceChips(item.prices, item.priceAsOf, item.sourceUrls),
    unitPrice: unitPrice(item),
    isBestValue: false,
  };
}

/** Decorates catalog items into render-ready rows (chips + unit price + best-value flag). */
export function buildResultRows(items: GroceryItem[]): GroceryResultRow[] {
  const bestIds = bestValueIds(items);
  return items.map((item) => ({ ...buildResultRow(item), isBestValue: bestIds.has(item.id) }));
}

/**
 * Whole-of-list comparison: per-store totals with missing items, the cheapest
 * store that carries everything, the split-across-stores total, and how much
 * splitting saves over the cheapest complete store.
 */
export function buildSummary(items: GroceryItem[]): ComparisonSummary {
  // Every item passed in is a catalog item (the caller filters out custom/unlisted
  // items on the honest genericItemId signal). A catalog item with missing prices is
  // a GENUINE miss — it must count as "missing" at every store, so a store that lacks
  // it is not reported as carrying everything. Do NOT drop priceless items here.
  const storeTotals: StoreTotal[] = GROCERY_STORES.map(({ key, label }) => {
    let total = 0;
    const missing: string[] = [];
    items.forEach((item) => {
      const price = item.prices[key];
      if (price != null) {
        total += price;
      } else {
        missing.push(item.name);
      }
    });
    return { store: key, label, total: roundCents(total), missing, isBestComplete: false };
  });

  // The best single-store trip: carries the most of the list, cheapest first among
  // ties. Ranking on total alone would crown a store carrying one $3 item over one
  // carrying the whole $45 list. A store carrying nothing isn't a real comparison
  // (this also covers the empty list, where every store trivially misses nothing).
  const topStore = storeTotals
    .filter((store) => store.missing.length !== items.length)
    .reduce<StoreTotal | null>(
      (best, store) =>
        best === null ||
        store.missing.length < best.missing.length ||
        (store.missing.length === best.missing.length && store.total < best.total)
          ? store
          : best,
      null,
    );
  // A complete store, when one exists, is always the winner above (missing.length 0
  // is the minimum), so this is the same store the old two-pass version picked.
  const bestComplete = topStore !== null && topStore.missing.length === 0 ? topStore : null;
  if (bestComplete !== null) {
    bestComplete.isBestComplete = true;
  }

  const splitTotal = roundCents(
    items.reduce((sum, item) => {
      const cheapest = cheapestStore(item.prices);
      return cheapest ? sum + (item.prices[cheapest] as number) : sum;
    }, 0),
  );

  const savingsFromSplit = bestComplete ? roundCents(bestComplete.total - splitTotal) : null;

  return {
    storeTotals,
    itemCount: items.length,
    splitTotal,
    bestCompleteStore: bestComplete ? bestComplete.store : null,
    bestCompleteLabel: bestComplete ? bestComplete.label : null,
    savingsFromSplit,
    bestStore: topStore ? topStore.store : null,
  };
}
